import * as Sentry from '@sentry/react';
import { makeNonSerializableStateStore, makeStateStore } from '@playcastdotio/StateStore';
import type { NonSerializableStateStore, StateStore } from '@playcastdotio/CoreTypes';
import { CoordinatorEventImpl } from '../_types/events';
import type { MediaState, MediaEvents } from './stores';
import { TrackCaptureManager } from './managers/TrackCaptureManager';
import { DeviceConfigManager } from './managers/DeviceConfigManager';
import { ShadowcamPresenceMonitor, type ShadowcamHealthSource } from './managers/ShadowcamPresenceMonitor';
import { ShadowcamCaptureManager } from './managers/capture/ShadowcamCaptureManager';
import type { PreparedVideoCapture } from './managers/TrackCaptureManager';
import type { QualityTierDemotionContext } from '../peer/managers/RecoveryCapacityGate';
import type { DriverState } from '@playcastdotio/CoreTypes';

const DIAG = '[AUDIO-BUG-12009:MediaCoordinator]';

type GetDriverStateFn = () => DriverState | Record<string, boolean> | undefined;

/**
 * MediaCoordinator - Owns all media capture and track lifecycle.
 *
 * Replaces the module-level `mediaManager.ts`. Provides a clean coordinator
 * interface for track capture, device management, and track lifecycle events.
 *
 * PeerCoordinator subscribes to `tracksChanged` to push new tracks to peers.
 */
export class MediaCoordinator {
  private mediaStore: NonSerializableStateStore<MediaState>;
  private trackCapture: TrackCaptureManager;
  private deviceConfig: DeviceConfigManager;
  private shadowcamPresenceMonitor: ShadowcamPresenceMonitor;
  private currentShadowcamName: string | null = null;
  private readonly shadowcamCapture: ShadowcamCaptureManager;

  public events: MediaEvents = {
    tracksChanged: new CoordinatorEventImpl(),
    deviceListChanged: new CoordinatorEventImpl(),
    videoMuteChanged: new CoordinatorEventImpl(),
    audioMuteChanged: new CoordinatorEventImpl(),
  };

  constructor(getDriverState: GetDriverStateFn = () => undefined) {
    this.mediaStore = makeNonSerializableStateStore<MediaState>({
      videoTrack: null,
      audioTrack: null,
      videoMuted: false,
      audioMuted: false,
      volume: 1.0,
      selectedCameraId: null,
      selectedMicrophoneId: null,
      selectedDesktopAudioSource: null,
      availableCameras: [],
      availableShadowcams: [],
      availableMicrophones: [],
      availableDesktopAudioSources: [],
    });

    this.trackCapture = new TrackCaptureManager();
    this.deviceConfig = new DeviceConfigManager();
    this.shadowcamCapture = new ShadowcamCaptureManager({ devices: this.deviceConfig, tracks: this.trackCapture,
      committed: name => { this.currentShadowcamName = name; } });
    this.shadowcamPresenceMonitor = new ShadowcamPresenceMonitor(this.deviceConfig, getDriverState);
  }

  public async init(): Promise<void> {
    console.log('[MediaCoordinator] Initializing...');

    this.trackCapture.setOnTrackChanged((videoTrack, audioTrack) => {
      this.updateTracksInStore(videoTrack, audioTrack);
    });

    this.deviceConfig.setOnDeviceListChanged(() => {
      this.syncDeviceListToStore();
      this.events.deviceListChanged.emit();
      this.shadowcamPresenceMonitor.refreshAfterEnumeration();
    });

    await this.deviceConfig.enumerateDevices();
    this.syncDeviceListToStore();
    this.shadowcamPresenceMonitor.reportLaunchPresence();

    // Subscribe to `devicechange` AFTER the initial enumerate so the launch-time
    // breadcrumb captures the boot snapshot, but subsequent changes (e.g. host-electron
    // activating ShadowCam03 ~700 ms after launch) still trigger refreshAfterEnumeration.
    this.deviceConfig.startDeviceChangeListener();

    console.log('[MediaCoordinator] Initialized');
  }

  public destroy(): void {
    console.log('[MediaCoordinator] Destroying...');
    this.shadowcamCapture.dispose();
    this.trackCapture.destroy();
    this.deviceConfig.destroy();
    this.shadowcamPresenceMonitor.destroy();
    console.log('[MediaCoordinator] Destroyed');
  }

  // ========================================
  // Public API - Track Access
  // ========================================

  public getVideoTrack(): MediaStreamTrack | null {
    return this.trackCapture.getVideoTrack();
  }

  /** Advertised capture rate for a device, in frames per second. */
  public frameRateForDevice(deviceId: string): number | undefined {
    return this.deviceConfig.frameRateForDevice(deviceId);
  }

  public getAudioTrack(): MediaStreamTrack | null {
    return this.trackCapture.getAudioTrack();
  }

  /**
   * Returns the name of the currently captured shadowcam, or null if none.
   * Used by PeerCoordinator to detect if a re-capture is needed when
   * the incoming peer requires a different shadowcam resolution.
   */
  public getCurrentShadowcamName(): string | null {
    return this.currentShadowcamName;
  }

  // ========================================
  // Public API - Track Capture
  // ========================================

  public async startVideoCapture(deviceId?: string): Promise<MediaStreamTrack | null> {
    const cameraId = deviceId ?? this.deviceConfig.getSelectedCameraId();
    if (!cameraId) {
      console.warn('[MediaCoordinator] No camera selected for video capture');
      return null;
    }

    this.deviceConfig.selectCamera(cameraId);
    const track = await this.trackCapture.captureVideo({ deviceId: { exact: cameraId } });
    return track;
  }

  public async startAudioCapture(deviceId?: string): Promise<MediaStreamTrack | null> {
    const micId = deviceId ?? this.deviceConfig.getSelectedMicrophoneId();
    if (!micId || micId === '0') {
      console.log('[MediaCoordinator] No microphone selected or "None" selected');
      return null;
    }

    this.deviceConfig.selectMicrophone(micId);
    const track = await this.trackCapture.captureAudio({ deviceId: { exact: micId } });
    return track;
  }

  public async startDesktopAudioCapture(): Promise<MediaStreamTrack | null> {
    this.deviceConfig.selectDesktopAudioSource('desktop');
    return await this.trackCapture.captureDesktopAudio();
  }

  /**
   * Capture video from a shadowcam by name match (e.g. 'ShadowCam03').
   * Falls back to first available shadowcam if name not found.
   */
  public async restartShadowcamCapture(shadowcamName?: string): Promise<MediaStreamTrack | null> {
    this.stopVideoCapture();
    return this.startShadowcamCapture(shadowcamName);
  }

  public startShadowcamCapture(shadowcamName?: string, context?: { isCurrent: () => boolean }): Promise<MediaStreamTrack | null> {
    return this.shadowcamCapture.start(shadowcamName, context);
  }

  public prepareShadowcamCapture(shadowcamName: string, context: QualityTierDemotionContext): Promise<PreparedVideoCapture | null> {
    return this.shadowcamCapture.prepare(shadowcamName, context);
  }

  public commitPreparedShadowcam(shadowcamName: string, prepared: PreparedVideoCapture,
    context: { isCurrent: () => boolean }): MediaStreamTrack {
    const previousName = this.currentShadowcamName;
    if (!context.isCurrent()) throw new Error('Capture superseded');
    this.currentShadowcamName = shadowcamName;
    try { return this.shadowcamCapture.commit(shadowcamName, prepared, context); }
    catch (error) { this.currentShadowcamName = previousName; throw error; }
  }

  public discardPreparedShadowcam(prepared: PreparedVideoCapture): void { this.shadowcamCapture.discard(prepared); }
  public retireVideoTrack(track: MediaStreamTrack): void { this.trackCapture.retireVideoTrack(track); }

  /**
   * Capture audio from AudioCaster virtual device (game audio capture).
   * Uses getUserMedia with the AudioCaster's deviceId.
   */
  public async startGameAudioCapture(): Promise<MediaStreamTrack | null> {
    await this.deviceConfig.enumerateDevices();
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioInputs = devices.filter((d) => d.kind === 'audioinput');
    const audioInputLabels = audioInputs.map((d) => d.label || '<empty-label>');

    console.log(`${DIAG} Device enumeration: ${audioInputs.length} audioinput(s)`, audioInputLabels);

    const audioCaster = audioInputs.find((d) => d.label.includes('AudioCaster Virtual Device'));

    if (!audioCaster) {
      console.warn(`${DIAG} AudioCaster NOT FOUND among ${audioInputs.length} audioinput devices`, audioInputLabels);
      Sentry.captureMessage('[AUDIO-BUG-12009] AudioCaster virtual device not found', {
        level: 'warning',
        tags: { audio_stage: 'device_lookup', audio_diag: 'AUDIO-BUG-12009' },
        contexts: {
          audioDevices: {
            audioInputCount: audioInputs.length,
            audioInputLabels: audioInputLabels.join(', '),
            emptyLabelCount: audioInputs.filter((d) => !d.label).length,
          },
        },
      });
      return null;
    }

    console.log(`${DIAG} AudioCaster found: "${audioCaster.label}" (${audioCaster.deviceId})`);
    Sentry.addBreadcrumb({
      category: 'AUDIO-BUG-12009',
      message: 'AudioCaster device found, initiating capture',
      level: 'info',
      data: { label: audioCaster.label, deviceId: audioCaster.deviceId, totalAudioInputs: audioInputs.length },
    });

    const track = await this.trackCapture.captureAudio({
      deviceId: { exact: audioCaster.deviceId },
      channelCount: 2,
      autoGainControl: false,
      noiseSuppression: false,
      echoCancellation: false,
    });
    if (!track) {
      console.warn(`${DIAG} captureAudio returned null for AudioCaster deviceId=${audioCaster.deviceId}`);
    }
    return track;
  }

  public stopVideoCapture(): void {
    this.trackCapture.stopVideoTrack();
    this.currentShadowcamName = null;
    this.updateTracksInStore(null, this.trackCapture.getAudioTrack());
  }

  public stopAudioCapture(): void {
    this.trackCapture.stopAudioTrack();
    this.updateTracksInStore(this.trackCapture.getVideoTrack(), null);
  }

  public stopAllCapture(): void {
    this.trackCapture.stopVideoTrack();
    this.trackCapture.stopAudioTrack();
    this.currentShadowcamName = null;
    this.updateTracksInStore(null, null);
  }

  // ========================================
  // Public API - Device Configuration
  // ========================================

  public async refreshDeviceList(): Promise<void> {
    await this.deviceConfig.enumerateDevices();
  }

  public getDeviceConfig(): DeviceConfigManager {
    return this.deviceConfig;
  }

  public hasAudioCaster(): boolean {
    return this.deviceConfig
      .getMicrophones()
      .some((mic) => (mic.label || '').includes('AudioCaster Virtual Device'));
  }

  public getShadowcamPresenceMonitor(): ShadowcamHealthSource {
    return this.shadowcamPresenceMonitor;
  }

  public reportDriverStatus(): void {
    this.shadowcamPresenceMonitor.reportDriverStatus();
  }

  // ========================================
  // Public API - Mute / Volume
  // ========================================

  public setVideoMuted(muted: boolean): void {
    this.deviceConfig.setVideoMuted(muted);
    const videoTrack = this.trackCapture.getVideoTrack();
    if (videoTrack) {
      videoTrack.enabled = !muted;
    }
    this.mediaStore.updateState({
      ...this.mediaStore.currentState,
      videoMuted: muted,
    });
    this.events.videoMuteChanged.emit(muted);
  }

  public setAudioMuted(muted: boolean): void {
    this.deviceConfig.setAudioMuted(muted);
    const audioTrack = this.trackCapture.getAudioTrack();
    if (audioTrack) {
      audioTrack.enabled = !muted;
    }
    this.mediaStore.updateState({
      ...this.mediaStore.currentState,
      audioMuted: muted,
    });
    this.events.audioMuteChanged.emit(muted);
  }

  /**
   * Set capture gain applied to all outgoing audio. `volume` is a linear
   * multiplier (0 = silence, 1.0 = unity, no boost). Drives the GainNode in
   * TrackCaptureManager's Web Audio pipeline so the slider actually changes
   * what peers hear; persists across capture restarts.
   */
  public setVolume(volume: number): void {
    this.deviceConfig.setVolume(volume);
    this.trackCapture.setAudioGain(volume);
    this.mediaStore.updateState({
      ...this.mediaStore.currentState,
      volume,
    });
  }

  // ========================================
  // Internal
  // ========================================

  private updateTracksInStore(videoTrack: MediaStreamTrack | null, audioTrack: MediaStreamTrack | null): void {
    this.mediaStore.updateState({
      ...this.mediaStore.currentState,
      videoTrack,
      audioTrack,
    });
    this.events.tracksChanged.emit({ videoTrack, audioTrack });
  }

  private syncDeviceListToStore(): void {
    this.mediaStore.updateState({
      ...this.mediaStore.currentState,
      availableCameras: this.deviceConfig.getCameras(),
      availableShadowcams: this.deviceConfig.getShadowcams(),
      availableMicrophones: this.deviceConfig.getMicrophones(),
      availableDesktopAudioSources: this.deviceConfig.getDesktopAudioSources(),
    });
  }
}
